/**
 * When does an agent actually get paid, and will they?
 *
 * The agent PWA showed an accrued balance and nothing else. An agent looking at
 * "RM 84.00 accrued" has two questions the screen did not answer — *when does
 * that arrive*, and *why has it not already* — and the second one is the
 * dangerous one: an agent who assumes they have been shorted stops selling.
 *
 * Both answers already existed as data (`thresholdSen` on the agent's payout
 * details, `payoutDayOfMonth` in the commission rules); nothing joined them up.
 *
 * Pure and clock-injected, so the same computation runs in the UI, in a test,
 * and in a notification without three versions of the month arithmetic.
 */

export interface NextPayoutPreview {
  /** Sen currently accrued and unpaid. */
  readonly accruedSen: number;
  /** Sen required before a payout runs at all. */
  readonly thresholdSen: number;
  /** True when this balance would be paid on the next run. */
  readonly reachesThreshold: boolean;
  /** Sen still needed to reach the threshold. 0 once it is met. */
  readonly shortfallSen: number;
  /** Epoch ms of the next payout run — the next occurrence of the payout day. */
  readonly nextRunMs: number;
}

/**
 * Compute the preview.
 *
 * `payoutDayOfMonth` is clamped into a real day, and a month that is too short
 * for it pays on its last day rather than silently rolling into the next month:
 * an agent told "the 31st" must still be paid in February.
 */
export function nextPayoutPreview(params: {
  readonly accruedSen: number;
  readonly thresholdSen: number;
  readonly payoutDayOfMonth: number;
  readonly nowMs: number;
}): NextPayoutPreview {
  const accruedSen = safeMoney(params.accruedSen);
  const thresholdSen = safeMoney(params.thresholdSen);
  const day = clampDay(params.payoutDayOfMonth);

  const now = new Date(params.nowMs);
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();

  // This month's run, if it has not already passed. `>` not `>=`: a run happens
  // ON its day, so an agent looking at the screen that morning should be told
  // today, not in a month's time.
  const thisMonth = Date.UTC(year, month, dayInMonth(year, month, day));
  const nextRunMs =
    thisMonth > params.nowMs
      ? thisMonth
      : Date.UTC(year, month + 1, dayInMonth(year, month + 1, day));

  const reachesThreshold = accruedSen >= thresholdSen && accruedSen > 0;

  return {
    accruedSen,
    thresholdSen,
    reachesThreshold,
    shortfallSen: reachesThreshold ? 0 : Math.max(0, thresholdSen - accruedSen),
    nextRunMs,
  };
}

/** A payout day outside 1–28 is a configuration error, not a reason to crash. */
function clampDay(day: number): number {
  if (!Number.isFinite(day)) return 1;
  return Math.min(31, Math.max(1, Math.trunc(day)));
}

/** The requested day, or the last day of that month when it is shorter. */
function dayInMonth(year: number, monthIndex: number, day: number): number {
  const lastDay = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
  return Math.min(day, lastDay);
}

function safeMoney(v: number): number {
  if (!Number.isFinite(v) || v <= 0) return 0;
  return Math.trunc(v);
}
